import type {
  LiveEffectEvent,
  LiveEffectOverlay,
} from '../../api/payment/live-effect.api';
import { clampEffectDurationSec } from '../../utils/live-effect';
import type { ObsRunner } from './page-live-effect-livestream.component';

/** Hạn chót cho một trò mạnh: thời lượng (kẹp 3–15 giây) cộng 5 giây dự phòng. */
export const obsTimeoutMs = (durationSec: number) =>
  (clampEffectDurationSec(durationSec) + 5) * 1000;

/** Chỉ lấy lượt mới khi không có lượt đang chạy và cấu hình overlay đã tải, đang bật. */
export const canPump = (running: boolean, overlay: LiveEffectOverlay | null) =>
  !running && !!overlay?.isEnabled;

/**
 * Chạy trò mạnh và gọi onDone đúng một lần: khi trò xong, khi trò lỗi, hoặc khi quá hạn chót.
 * Quá hạn thì trả OBS về (bỏ qua lỗi) để lệnh OBS treo không chặn hàng đợi mãi.
 */
export const runObsWithTimeout = (
  runner: ObsRunner,
  ev: LiveEffectEvent,
  overlay: LiveEffectOverlay,
  onDone: (note?: string) => void
) => {
  let done = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settle = (note?: string) => {
    if (done) return;
    done = true;
    if (timer !== undefined) clearTimeout(timer);
    onDone(note);
  };
  const onTimeout = () => {
    if (done) return;
    try {
      runner.reset().catch(() => undefined);
    } catch {
      /* reset ném đồng bộ: vẫn giải phóng hàng đợi */
    }
    settle();
  };
  timer = setTimeout(onTimeout, obsTimeoutMs(ev.durationSec));
  // Trò phải chờ khoá OBS chung (nguồn overlay khác đang chạy trò) thì hạn chót tính lại từ lúc trò bắt đầu thật.
  const onStart = () => {
    if (done) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(onTimeout, obsTimeoutMs(ev.durationSec));
  };
  let running: Promise<'ok' | 'source_missing'>;
  try {
    running = runner.run(ev, overlay, { onStart });
  } catch (e) {
    running = Promise.reject(e);
  }
  running
    .then((res) =>
      settle(res === 'source_missing' ? 'source_missing' : undefined)
    )
    .catch(() => settle());
};
